/**
 * ChipFilter — `.chip-filter` from the prototype (cms-redesign-mockup.html
 * ~line 862–878). Optional, dashed-outline filter pill that becomes a
 * solid teal-tinted chip when active.
 *
 *   <ChipFilter onClick={…}>Recommended</ChipFilter>            // inactive
 *   <ChipFilter active onClick={…}>Recommended</ChipFilter>     // active, no × (segmented)
 *   <ChipFilter active removable onClick={…}>Free</ChipFilter>  // active, × suffix
 *
 * Inactive state is a dashed-outline pill — the dashed border alone signals
 * "this filter is available but not applied" (the old `+` prefix was dropped:
 * it read as "add" rather than "filter", Monday 12171287315). Active state
 * collapses the dashed border to solid teal-green and adds a × close
 * affordance when `removable`.
 */
import React from 'react';
import { cn } from './cn';

export function ChipFilter({
  active = false,
  onClick,
  removable = false,
  children,
  className,
  ...rest
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center font-semibold whitespace-nowrap',
        'rounded-pill cursor-pointer',
        // Hover transitions to solid teal regardless of active state.
        'hover:border-teal-green hover:text-teal-green-dark',
        active
          ? // `.chip-filter.active` (874–877): solid teal border + teal-soft bg.
            'border border-solid border-teal-green bg-teal-green-soft text-teal-green-dark'
          : // `.chip-filter` resting state (862–869): DASHED border,
            // line-strong color, transparent bg, text-light.
            'border border-dashed border-line-strong text-text-light bg-transparent hover:bg-surface',
        className,
      )}
      style={{
        height: 30,
        padding: '0 12px',
        gap: 6,
        fontSize: 12.5,
        transition: 'all 160ms ease',
      }}
      {...rest}
    >
      <span>{children}</span>
      {/* `.x` (878) — 14px / 0.7 opacity. Only shown for removable chips. */}
      {active && removable && (
        <span aria-hidden style={{ fontSize: 14, lineHeight: 1, opacity: 0.7 }}>×</span>
      )}
    </button>
  );
}

export default ChipFilter;
